import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Accordion, Button } from 'flowbite-react';
import { useTranslation } from 'react-i18next';
import MDEditor, { commands, ICommand } from '@uiw/react-md-editor';
import {
  ImageIcon,
  Copy,
  Check,
  ArrowLeft,
  ArrowRight,
  ChevronDown,
} from 'lucide-react';
import { useUserInfo } from '../auth/UserInfoProvider';
import {
  useFetchTicketByIdQuery,
  useUpdateTicketStatusMutation,
  useAddTicketReplyMutation,
} from '../redux/api/technicalSupportApi';
import { useCreateMediaMutation } from '../redux/api/mediaApi';
import { useAppSelector } from '../redux/hooks/useAppSelector';
import {
  selectIsDarkMode,
  selectIsRTL,
} from '../redux/selectors/displaySettingsSelectors';
import { getMediaType } from '../utils/getMediaType';
import { useMarkdownImageUpload } from '../hooks/useMarkdownImageUpload';
import { MediaPurpose } from '../common/enums/MediaPurpose.enum';
import { SupportStatus } from '../common/enums/SupportStatus';
import { Loading } from '../components/Shared/Loading/Loading';
import { ErrorComponent } from '../components/Shared/ErrorComponent/ErrorComponent';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  categoryStyles,
  unknownCategoryStyle,
  statusStyles,
} from '../components/TechnicalSupport/ticketStyles';

const TechnicalSupportDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userInfo } = useUserInfo();
  const isDarkMode = useAppSelector(selectIsDarkMode);
  const isRtl = useAppSelector(selectIsRTL);

  const [reply, setReply] = useState('');
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const {
    data: ticketData,
    isLoading,
    isError,
  } = useFetchTicketByIdQuery(id ?? '', { skip: !id, pollingInterval: 10000 });

  const ticket = ticketData?.data;

  usePageTitle(
    ticket
      ? t('technicalSupport.detailsPageTitle', {
          ticketNumber: ticket.ticketNumber,
        })
      : t('sidebar.technicalSupportManagement')
  );

  useEffect(() => {
    setReply('');
    setIsPreviewMode(false);
  }, [id]);

  const [updateTicketStatus, { isLoading: isUpdatingStatus }] =
    useUpdateTicketStatusMutation();
  const [addTicketReply, { isLoading: isSending }] =
    useAddTicketReplyMutation();
  const [createMedia] = useCreateMediaMutation();

  const { textareaProps: pasteDropProps, isUploading } = useMarkdownImageUpload(
    {
      value: reply,
      onChange: setReply,
      purpose: MediaPurpose.SupportTicket,
    }
  );

  const handleStatusChange = async (status: SupportStatus) => {
    if (!ticket) return;
    await updateTicketStatus({ id: ticket.id, status }).unwrap();
  };

  const handleSendReply = async () => {
    if (
      !ticket ||
      !reply.trim() ||
      !userInfo ||
      (!ticket.userId && !ticket.userEmail)
    )
      return;
    const message = reply.trim();
    setReply('');
    try {
      await addTicketReply({
        id: ticket.id,
        message,
        to: ticket.userId ?? ticket.userEmail ?? '',
        fromUser: { id: userInfo.id, name: userInfo.name },
      }).unwrap();
    } catch {
      setReply(message);
    }
  };

  const handleCopy = async (text: string, idx: number) => {
    await navigator.clipboard.writeText(text);
    setCopiedIndex(idx);
    setTimeout(() => setCopiedIndex((c) => (c === idx ? null : c)), 1500);
  };

  const createMediaRef = useRef(createMedia);
  createMediaRef.current = createMedia;

  const uploadImageCommand: ICommand = useMemo(
    () => ({
      name: 'upload-image',
      keyCommand: 'upload-image',
      buttonProps: {
        'aria-label': t('technicalSupport.uploadImage'),
        title: t('technicalSupport.uploadImage'),
      },
      icon: <ImageIcon size={13} />,
      execute: (_state, api) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async () => {
          const file = input.files?.[0];
          if (!file) return;
          const placeholder = `![${t('technicalSupport.uploadingImage')}]()`;
          api.replaceSelection(placeholder);
          try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('mediaType', getMediaType(file.type));
            fd.append('purpose', MediaPurpose.SupportTicket);
            // @ts-expect-error — RTK Query mutation types don't accept FormData directly
            const res = await createMediaRef.current(fd).unwrap();
            const currentText = api.textArea.value;
            const idx = currentText.indexOf(placeholder);
            if (idx >= 0)
              api.setSelectionRange({
                start: idx,
                end: idx + placeholder.length,
              });
            api.replaceSelection(`![](${res.url})`);
          } catch {
            const currentText = api.textArea.value;
            const idx = currentText.indexOf(placeholder);
            if (idx >= 0) {
              api.setSelectionRange({
                start: idx,
                end: idx + placeholder.length,
              });
              api.replaceSelection('');
            }
          }
        };
        input.click();
      },
    }),
    []
  );

  if (isLoading) return <Loading />;
  if (isError || !ticket) {
    return <ErrorComponent errorMessage={t('technicalSupport.notFound')} />;
  }

  const BackIcon = isRtl ? ArrowRight : ArrowLeft;
  const editorClass = `md-reply-editor ${isRtl ? 'rtl' : 'ltr'}`;
  const catStyle =
    (ticket.category && categoryStyles[ticket.category]) ??
    unknownCategoryStyle;
  const CatIcon = catStyle.Icon;

  return (
    <div className="flex flex-col gap-6">
      {/* ── Page header ── */}
      <div className="flex flex-col gap-3 rounded-lg border border-gray-300 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-gray-800">
        {/* Back nav */}
        <Button
          size="xs"
          color="light"
          onClick={() => navigate(-1)}
          className="w-fit"
        >
          <BackIcon size={14} className="me-1.5" aria-hidden="true" />
          {t('technicalSupport.back')}
        </Button>

        {/* Title row */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex flex-col gap-1.5">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {ticket.ticketNumber}
              </h1>
              {ticket.category && (
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${catStyle.className}`}
                >
                  <CatIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  {t(
                    'technicalSupport.categoryList.' +
                      ticket.category.toLowerCase()
                  )}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {[
                ticket.userEmail,
                new Date(ticket.createdAt).toLocaleString(isRtl ? 'ar' : 'en'),
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>

          {/* Status selector */}
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('technicalSupport.status')}
            </span>
            <div className="relative w-fit">
              <select
                id="ticket-status"
                disabled={isUpdatingStatus}
                value={ticket.status}
                onChange={(e) =>
                  handleStatusChange(e.target.value as SupportStatus)
                }
                className={`cursor-pointer appearance-none rounded-full py-1 pe-7 ps-3 text-xs font-semibold ring-1 ring-inset focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-60 ${statusStyles[ticket.status].className}`}
              >
                {Object.values(SupportStatus).map((s) => (
                  <option key={s} value={s}>
                    {t('technicalSupport.statusList.' + s.toLowerCase())}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={12}
                className="pointer-events-none absolute end-2 top-1/2 -translate-y-1/2"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Original message ── */}
      <section
        aria-labelledby="original-msg-heading"
        className="rounded-lg border border-gray-300 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-gray-800"
      >
        <h2
          id="original-msg-heading"
          className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300"
        >
          {t('technicalSupport.originalMessage')}
        </h2>
        <div className="auto-direction-text whitespace-pre-wrap text-gray-800 dark:text-gray-100">
          {ticket.message}
        </div>
      </section>

      {/* ── Replies ── */}
      <section
        aria-labelledby="replies-heading"
        className="flex flex-col gap-3 rounded-lg border border-gray-300 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-gray-800"
      >
        <h2
          id="replies-heading"
          className="text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300"
        >
          {t('technicalSupport.replies')}
          {(ticket.replies ?? []).length > 0 && (
            <span className="ms-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
              {ticket.replies!.length}
            </span>
          )}
        </h2>

        {/* Thread */}
        <div
          role="log"
          aria-live="polite"
          aria-label={t('technicalSupport.replies')}
          className="flex max-h-[45vh] flex-col gap-2 overflow-y-auto"
        >
          {(ticket.replies ?? []).length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-400">
              {t('technicalSupport.noReplies')}
            </p>
          ) : (
            (ticket.replies ?? []).map((r, i) => {
              const isMine = r.from === userInfo?.id;
              const isAdminReply = !ticket.userId || r.from !== ticket.userId;
              return (
                <div
                  key={i}
                  className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[75%] rounded-lg p-3 ${
                      isMine
                        ? 'bg-primary-100 dark:bg-primary-700'
                        : 'bg-gray-100 dark:bg-gray-700'
                    }`}
                  >
                    <div className="mb-1 flex items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span>
                        {r.fromUser?.name ?? r.from} ·{' '}
                        {new Date(r.sendAt).toLocaleString()}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleCopy(r.message, i)}
                        title={
                          copiedIndex === i
                            ? t('technicalSupport.copied')
                            : t('technicalSupport.copy')
                        }
                        aria-label={t('technicalSupport.copy')}
                        className="flex shrink-0 items-center rounded p-1 hover:bg-black/5 dark:hover:bg-white/10"
                      >
                        {copiedIndex === i ? (
                          <Check size={14} />
                        ) : (
                          <Copy size={14} />
                        )}
                      </button>
                    </div>
                    {isAdminReply ? (
                      <div
                        className="markdown-reply auto-direction-text text-sm text-gray-900 dark:text-gray-100"
                        data-color-mode={isDarkMode ? 'dark' : 'light'}
                      >
                        <MDEditor.Markdown source={r.message} />
                      </div>
                    ) : (
                      <div className="auto-direction-text whitespace-pre-wrap text-sm text-gray-900 dark:text-gray-100">
                        {r.message}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Reply form */}
        <div className="flex flex-col gap-2 border-t border-gray-100 pt-3 dark:border-gray-700">
          <MDEditor
            className={editorClass}
            data-color-mode={isDarkMode ? 'dark' : 'light'}
            value={reply}
            onChange={(v) => setReply(v ?? '')}
            textareaProps={{
              ...pasteDropProps,
              disabled: isSending || isUploading,
              placeholder: t('technicalSupport.replyPlaceholder'),
            }}
            commands={[
              commands.bold,
              commands.italic,
              commands.strikethrough,
              commands.divider,
              commands.link,
              commands.quote,
              commands.unorderedListCommand,
              commands.orderedListCommand,
              commands.divider,
              uploadImageCommand,
            ]}
            extraCommands={[]}
            preview={isPreviewMode ? 'preview' : 'edit'}
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              color="gray"
              onClick={() => setIsPreviewMode((p) => !p)}
            >
              {isPreviewMode
                ? t('technicalSupport.editMode')
                : t('technicalSupport.previewMode')}
            </Button>
            <Button
              size="sm"
              className="btn btn-light ms-auto"
              disabled={
                !reply.trim() ||
                isSending ||
                isUploading ||
                !userInfo ||
                (!ticket.userId && !ticket.userEmail)
              }
              onClick={handleSendReply}
              isProcessing={isSending}
            >
              {t('technicalSupport.sendReply')}
            </Button>
          </div>
        </div>
      </section>

      {/* ── Device & ticket details ── */}
      <Accordion className="border-primary-100">
        <Accordion.Panel>
          <Accordion.Title className="accordion">
            {t('technicalSupport.ticketDetails')}
          </Accordion.Title>
          <Accordion.Content className="bg-transparent p-4 dark:bg-transparent">
            <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <MetaRow
                label={t('technicalSupport.ticketNumber')}
                value={ticket.ticketNumber}
              />
              {ticket.userEmail && (
                <MetaRow
                  label={t('technicalSupport.userEmail')}
                  value={ticket.userEmail}
                />
              )}
              {ticket.mobileNumber && (
                <MetaRow
                  label={t('technicalSupport.mobileNumber')}
                  value={ticket.mobileNumber}
                />
              )}
              <MetaRow
                label={t('technicalSupport.createdAt')}
                value={new Date(ticket.createdAt).toLocaleString()}
              />
              <MetaRow
                label={t('technicalSupport.status')}
                value={t(
                  'technicalSupport.statusList.' + ticket.status.toLowerCase()
                )}
              />
              <MetaRow
                label={t('technicalSupport.deviceId')}
                value={
                  ticket.device.deviceId || t('technicalSupport.notAvailable')
                }
              />
              <MetaRow
                label={t('technicalSupport.deviceModel')}
                value={
                  ticket.device.deviceModel ||
                  t('technicalSupport.notAvailable')
                }
              />
              <MetaRow
                label={t('technicalSupport.osType')}
                value={
                  ticket.device.osType || t('technicalSupport.notAvailable')
                }
              />
              <MetaRow
                label={t('technicalSupport.osVersion')}
                value={
                  ticket.device.osVersion || t('technicalSupport.notAvailable')
                }
              />
              <MetaRow
                label={t('technicalSupport.manufacturer')}
                value={
                  ticket.device.manufacturer ||
                  t('technicalSupport.notAvailable')
                }
              />
              <MetaRow
                label={t('technicalSupport.brand')}
                value={
                  ticket.device.brand || t('technicalSupport.notAvailable')
                }
              />
              <MetaRow
                label={t('technicalSupport.buildNumber')}
                value={
                  ticket.device.buildNumber ||
                  t('technicalSupport.notAvailable')
                }
              />
              <MetaRow
                label={t('technicalSupport.screenWidth')}
                value={
                  ticket.device.screenWidth
                    ? `${ticket.device.screenWidth}px`
                    : t('technicalSupport.notAvailable')
                }
              />
              <MetaRow
                label={t('technicalSupport.screenHeight')}
                value={
                  ticket.device.screenHeight
                    ? `${ticket.device.screenHeight}px`
                    : t('technicalSupport.notAvailable')
                }
              />
              <MetaRow
                label={t('technicalSupport.orientation')}
                value={
                  ticket.device.orientation ||
                  t('technicalSupport.notAvailable')
                }
              />
              <MetaRow
                label={t('technicalSupport.appVersion')}
                value={ticket.device.appVersion}
              />
              <MetaRow
                label={t('technicalSupport.timezone')}
                value={ticket.device.timezone}
              />
              <MetaRow
                label={t('technicalSupport.languageIsoCode')}
                value={ticket.device.languageIsoCode}
              />
              {ticket.device.userLocation?.coordinates &&
                ticket.device.userLocation.coordinates.length > 0 && (
                  <div className="flex flex-row items-center justify-between gap-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900/40 sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      {t('technicalSupport.coordinates')}
                    </dt>
                    <dd>
                      <a
                        href={`https://www.google.com/maps/?q=${ticket.device.userLocation.coordinates[1]},${ticket.device.userLocation.coordinates[0]}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="auto-direction-text text-sm text-primary-600 underline hover:text-primary-700 dark:text-primary-300 dark:hover:text-primary-200"
                      >
                        {ticket.device.userLocation.coordinates.join(', ')}
                      </a>
                    </dd>
                  </div>
                )}
            </dl>
          </Accordion.Content>
        </Accordion.Panel>
      </Accordion>
    </div>
  );
};

export default TechnicalSupportDetailsPage;

const MetaRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-row items-center justify-between gap-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900/40">
    <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">
      {label}
    </dt>
    <dd className="text-sm font-semibold text-gray-900 dark:text-white">
      {value}
    </dd>
  </div>
);
